import { Box, Flex } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import type React from 'react';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import * as yup from 'yup';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftDataLoader from '@/components/Atoms/FaceliftDataLoader';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import type { ExitConfirmationDraftAction } from '@/components/Molecules/FaceliftDialogWithExitConfirmation';
import FaceliftSuccessProgressModal from '@/components/Molecules/FaceliftSuccessProgressModal';
import Show from '@/components/Molecules/Show';
import { Stepper, type StepperStep } from '@/components/Organisms/Stepper';
import { bvServiceLocation, DRAFT_TYPE_LABELS, FORM_TYPES } from '@/constants';
import Preview from '@/components/Templates/BenefitVerification/Steps/Preview';
import type { UploadedFile } from '@/hooks';
import { useGetSingleBV } from '@/hooks/service-hooks/benefitsVerification';
import {
	BgProcessModule,
	useCreateBgProcess,
	useUpdateBgProcess,
} from '@/hooks/service-hooks/bgProcess';
import {
	useSaveDraft,
} from '@/hooks/service-hooks/drafts';
import { useAuthStore } from '@/store';
import { convertToISODateFormat, formatDate } from '@/utils';
import type { BenefitVerificationData } from '../BenefitVerificationForm/types';
import {
	FacilityInformation,
	PatientInformation,
	Product,
	UploadDocuments,
	WoundInformation,
} from '../Steps';
import { fieldLabelMap } from './constants';
import type { Draft, DraftFormSuccessResult } from '@/types/draft';
import { trackCoreClick } from '@/utils/analytics';

const bvSchema = yup.object().shape({
	clinic: yup.object<SelectItem>().required('Clinic is required'),
	provider: yup.object<SelectItem>().required('Provider is required'),
	patient: yup.object<SelectItem>().required('Patient is required'),
	patientFirstName: yup.string().required('First name is required'),
	patientLastName: yup.string().required('Last name is required'),
	physicianFirstName: yup.string().optional().default(''),
	physicianLastName: yup.string().optional().default(''),
	birthdate: yup.string().required('Birth date is required'),
	primaryInsurance: yup
		.object<SelectItem>()
		.required('Primary insurance is required'),
	ta_primarymemberid: yup.string().required('Primary member ID is required'),
	secondaryInsurance: yup
		.object<SelectItem>()
		.required('Secondary insurance is required'),
	ta_secondarymemberid: yup.string().optional().default(''),
	ta_emrid: yup.string().optional().default(''),
	mobilephone: yup.string().optional().default(''),
	emailaddress1: yup
		.string()
		.email('Invalid email format')
		.optional()
		.default(''),
	ta_nursinghome: yup.string().optional().default('No'),
	ta_100days: yup.string().optional().default('No'),

	ta_productselection: yup
		.string()
		.default('')
		.required('Product selection is required'),
	ta_servicelocation: yup.object<SelectItem>().required('Service location is required'),
	oldProducts: yup.string().optional().default(''),

	productSelectedNames: yup.string().default(''),
	ta_noteshartreview: yup.string().optional().default(''),

	totalWoundsCreated: yup.number().optional().default(1),

	ta_proceduredate: yup
		.string()
		.default('')
		.required('Procedure Date is required'),
	ta_woundlength: yup
		.number()
		.default(null)
		.nullable()
		.required('Wound Length is required'),
	ta_woundwidth: yup.number().default(null).required('Wound Width is required'),
	ta_wounddepth: yup.number().optional().default(null).nullable(),
	ta_woundsizetotal2: yup.number().optional().default(null).nullable(),
	ta_woundtype1: yup.object<SelectItem>().default(null).nullable(),

	'ta_ApplicationCode1@odatabind': yup
		.object<SelectItem>()
		.required('Application Code 1 is required'), // No dot here
	'ta_ApplicationCode2@odatabind': yup
		.object<SelectItem>()
		.default(null)
		.nullable(),
	'ta_DiagnosisCode1@odatabind': yup
		.object<SelectItem>()
		.required('Primary Diagnosis is required'),
	'ta_DiagnosisCode2@odatabind': yup
		.object<SelectItem>()
		.default(null)
		.nullable(),
	'ta_DiagnosisCode3@odatabind': yup
		.object<SelectItem>()
		.default(null)
		.nullable(),
	'ta_DiagnosisCode4@odatabind': yup
		.object<SelectItem>()
		.default(null)
		.nullable(),
	ta_wound2_woundlength: yup
		.number()
		.optional()
		.default(null)
		.nullable()
		.when('totalWoundsCreated', {
			is: (val: number) => val && val >= 2,
			then: (schema) =>
				schema
					.typeError('Wound 2 Length is required')
					.required('Wound 2 Length is required'),
		}),
	ta_wound2_woundwidth: yup
		.number()
		.optional()
		.default(null)
		.nullable()
		.when('totalWoundsCreated', {
			is: (val: number) => val && val >= 2,
			then: (schema) =>
				schema
					.typeError('Wound 2 Width is required')
					.required('Wound 2 Width is required'),
		}),
	ta_wound2_woundsizetotal: yup.number().optional().default(null).nullable(),
	ta_wound2_wounddepth: yup
		.number()
		.optional()
		.default(null)
		.nullable(),
	ta_woundtype2: yup.object<SelectItem>().default(null).nullable(),
	'ta_Wound2_Application1@odatabind': yup
		.object<SelectItem>()
		.default(null)
		.nullable()
		.when('totalWoundsCreated', {
			is: (val: number) => val && val >= 2,
			then: (schema) => schema.required('Wound 2 Application Code 1 is required'),
		}),
	'ta_Wound2_Application2@odatabind': yup
		.object<SelectItem>()
		.default(null)
		.nullable(),
	'ta_Wound2_Diagnosis1@odatabind': yup
		.object<SelectItem>()
		.default(null)
		.nullable()
		.when('totalWoundsCreated', {
			is: (val: number) => val && val >= 2,
			then: (schema) => schema.required('Wound 2 Primary Diagnosis is required'),
		}),
	'ta_Wound2_Diagnosis2@odatabind': yup
		.object<SelectItem>()
		.default(null)
		.nullable(),
	'ta_Wound2_Diagnosis3@odatabind': yup
		.object<SelectItem>()
		.default(null)
		.nullable(),
	'ta_Wound2_Diagnosis4@odatabind': yup
		.object<SelectItem>()
		.default(null)
		.nullable(),

	ta_wound3_woundlength: yup
		.number()
		.optional()
		.default(null)
		.nullable()
		.when('totalWoundsCreated', {
			is: (val: number) => val && val >= 3,
			then: (schema) =>
				schema
					.typeError('Wound 3 Length is required')
					.required('Wound 3 Length is required'),
		}),
	ta_wound3_woundwidth: yup
		.number()
		.optional()
		.default(null)
		.nullable()
		.when('totalWoundsCreated', {
			is: (val: number) => val && val >= 3,
			then: (schema) =>
				schema
					.typeError('Wound 3 Width is required')
					.required('Wound 3 Width is required'),
		}),
	ta_wound3_wounddepth: yup
		.number()
		.optional()
		.default(null)
		.nullable(),
	ta_wound3_woundsizetotal: yup.number().optional().default(null).nullable(),
	ta_woundtype3: yup.object<SelectItem>().default(null).nullable(),
	'ta_Wound3_Application1@odatabind': yup
		.object<SelectItem>()
		.default(null)
		.nullable()
		.when('totalWoundsCreated', {
			is: (val: number) => val && val >= 3,
			then: (schema) => schema.required('Wound 3 Application Code 1 is required'),
		}),
	'ta_Wound3_Application2@odatabind': yup
		.object<SelectItem>()
		.default(null)
		.nullable(),
	'ta_Wound3_Diagnosis1@odatabind': yup
		.object<SelectItem>()
		.default(null)
		.nullable()
		.when('totalWoundsCreated', {
			is: (val: number) => val && val >= 3,
			then: (schema) => schema.required('Wound 3 Primary Diagnosis is required'),
		}),
	'ta_Wound3_Diagnosis2@odatabind': yup
		.object<SelectItem>()
		.default(null)
		.nullable(),
	'ta_Wound3_Diagnosis3@odatabind': yup
		.object<SelectItem>()
		.default(null)
		.nullable(),
	'ta_Wound3_Diagnosis4@odatabind': yup
		.object<SelectItem>()
		.default(null)
		.nullable(),

	updatedFiles: yup
		.array()
		.of(
			yup.object().shape({
				id: yup.string().required(),
				name: yup.string().required(),
				size: yup.number().required(),
				type: yup.string().required(),
				file: yup.mixed().optional(),
				sharePointUrl: yup.string().required(),
				sharePointFileName: yup.string().required(),
				clinic: yup.string().required(),
				folderName: yup.string().required(),
			}),
		)
		.optional()
		.default([]),

	ta_portalbvupload: yup.string().optional().default(null),
});

export type BVFormData = yup.InferType<typeof bvSchema>;

interface BenefitVerificationProps {
	onIsDirtyChange?: (isDirty: boolean) => void;
	onSuccess: (result?: DraftFormSuccessResult) => void;
	id?: string | null;
	customFunction?: (data?: { size?: string; step?: number }) => void;
	existingDraft?: Draft;
	onExitConfirmationDraftActionChange?: (
		action: ExitConfirmationDraftAction | null,
	) => void;
}

const BenefitVerification: React.FC<BenefitVerificationProps> = ({
	onSuccess,
	id = '',
	customFunction,
	onIsDirtyChange,
	existingDraft,
	onExitConfirmationDraftActionChange,
}) => {
	// `id` is the CRM Benefit Verification id, not a draft id.
	const benefitVerificationId = id ?? '';
	const hasExistingBenefitVerification = benefitVerificationId.length > 0;

	const {
		register,
		formState: { errors, isDirty, dirtyFields },
		setValue,
		getValues,
		watch,
		reset,
		control,
		trigger,
		setError,
	} = useForm<BVFormData>({
		resolver: yupResolver(bvSchema),
		mode: 'onSubmit',
		defaultValues: {
			clinic: undefined,
			provider: undefined,
			patientFirstName: undefined,
			patientLastName: undefined,
			physicianFirstName: '',
			physicianLastName: '',
			patient: undefined,
			birthdate: undefined,
			primaryInsurance: undefined,
			ta_primarymemberid: undefined,
			ta_secondarymemberid: '',
			secondaryInsurance: undefined,
			ta_emrid: '',
			mobilephone: '',
			emailaddress1: '',
			ta_nursinghome: 'No',
			ta_100days: 'No',
			ta_productselection: '',
			oldProducts: '',
			productSelectedNames: '',
			ta_noteshartreview: '',
			totalWoundsCreated: 1,
			ta_proceduredate: '',
			ta_woundlength: null as unknown as number | undefined,
			ta_woundwidth: null as unknown as number | undefined,
			ta_wounddepth: null,
			ta_woundsizetotal2: null,
			ta_woundtype1: null,
			'ta_ApplicationCode1@odatabind': undefined,
			'ta_ApplicationCode2@odatabind': null,
			'ta_DiagnosisCode1@odatabind': undefined,
			'ta_DiagnosisCode2@odatabind': null,
			'ta_DiagnosisCode3@odatabind': null,
			'ta_DiagnosisCode4@odatabind': null,
			ta_wound2_woundlength: null,
			ta_wound2_woundwidth: null,
			ta_wound2_wounddepth: null,
			ta_wound2_woundsizetotal: null,
			ta_woundtype2: null,
			'ta_Wound2_Application1@odatabind': null,
			'ta_Wound2_Application2@odatabind': null,
			'ta_Wound2_Diagnosis1@odatabind': null,
			'ta_Wound2_Diagnosis2@odatabind': null,
			'ta_Wound2_Diagnosis3@odatabind': null,
			'ta_Wound2_Diagnosis4@odatabind': null,
			ta_wound3_woundlength: null,
			ta_wound3_woundwidth: null,
			ta_wound3_wounddepth: null,
			ta_wound3_woundsizetotal: null,
			ta_woundtype3: null,
			'ta_Wound3_Application1@odatabind': null,
			'ta_Wound3_Application2@odatabind': null,
			'ta_Wound3_Diagnosis1@odatabind': null,
			'ta_Wound3_Diagnosis2@odatabind': null,
			'ta_Wound3_Diagnosis3@odatabind': null,
			'ta_Wound3_Diagnosis4@odatabind': null,
			updatedFiles: [],
			ta_portalbvupload: null as unknown as string | undefined,
			ta_servicelocation: null as unknown as SelectItem | undefined,
		},
	});
	const user = useAuthStore((state) => state.user);
	const userId = user?.userInformation?._id || null;
	const userName = user?.userInformation ? `${user.userInformation.firstName} ${user.userInformation.lastName}` : '';

	const { mutate: getSingleBVDetails, isLoading: isLoadingSingleBV } =
		useGetSingleBV((bvRecord) => {
			if (bvRecord) {
				const formData: Partial<BVFormData> = {
					clinic: {
						label: bvRecord?.account?.name,
						value: bvRecord?.account?.accountid,
					},
					provider: {
						label: bvRecord?.provider?.fullname || '',
						value: bvRecord.provider?.contactid
							? `{${bvRecord.provider.contactid}}`
							: '',
					},
					ta_servicelocation: {
						label: bvServiceLocation.find(
							(item) =>
								item.value === bvRecord?.ta_servicelocation,
						)?.label || '',
						value: bvRecord?.ta_servicelocation,
					},
						
					physicianFirstName: bvRecord?.provider?.firstname || '',
					physicianLastName: bvRecord?.provider?.lastname || '',
					patient: {
						label: bvRecord?.patient?.fullname || '',
						value: bvRecord.patient?.contactid || '',
					},
					primaryInsurance: {
						label: bvRecord?.primaryInsurance?.ta_name || '',
						value: bvRecord.primaryInsurance?.ta_insuranceid || '',
					},
					secondaryInsurance: bvRecord?.secondaryInsurance?.ta_name
						? {
								label: bvRecord?.secondaryInsurance?.ta_name || '',
								value: bvRecord.secondaryInsurance?.ta_insuranceid || '',
							}
						: undefined,
					birthdate: formatDate(bvRecord.ta_dob) || '',
					ta_emrid: bvRecord.ta_emrid || '',
					ta_primarymemberid: bvRecord.ta_primarymemberid || '',
					ta_secondarymemberid: bvRecord.ta_secondarymemberid || '',
					ta_nursinghome: bvRecord.ta_nursinghome ? 'yes' : 'no',
					ta_100days: bvRecord.ta_100days ? 'yes' : 'no',
					patientFirstName: bvRecord.patient?.firstname || '',
					patientLastName: bvRecord.patient?.lastname || '',
					ta_productselection: bvRecord?.ta_productselection || '',
					oldProducts: bvRecord?.ta_productselection || '',
					ta_proceduredate: formatDate(bvRecord?.ta_proceduredate) || '',
					totalWoundsCreated: 1,
					updatedFiles: [],
					ta_portalbvupload: bvRecord?.ta_portalbvupload || undefined,
				};

				// Wound 1 fields
				if (bvRecord?.woundOneApplicationCode1) {
					formData['ta_ApplicationCode1@odatabind'] = {
						label: bvRecord?.woundOneApplicationCode1?.ta_name || '',
						value:
							bvRecord?.woundOneApplicationCode1?.ta_applicationcodesid || '',
					};
				}
				if (bvRecord?.woundOneApplicationCode2) {
					formData['ta_ApplicationCode2@odatabind'] = {
						label: bvRecord?.woundOneApplicationCode2?.ta_name || '',
						value:
							bvRecord?.woundOneApplicationCode2?.ta_applicationcodesid || '',
					};
				}
				if (bvRecord?.woundOneDiagnosisCode1) {
					formData['ta_DiagnosisCode1@odatabind'] = {
						label: bvRecord?.woundOneDiagnosisCode1?.ta_name || '',
						value: bvRecord?.woundOneDiagnosisCode1?.ta_diagnosiscodeid || '',
					};
					formData['ta_DiagnosisCode2@odatabind'] = {
						label: bvRecord?.woundOneDiagnosisCode1?.ta_name || '',
						value: bvRecord?.woundOneDiagnosisCode1?.ta_diagnosiscodeid || '',
					};
					formData['ta_DiagnosisCode3@odatabind'] = {
						label: bvRecord?.woundOneDiagnosisCode1?.ta_name || '',
						value: bvRecord?.woundOneDiagnosisCode1?.ta_diagnosiscodeid || '',
					};
					formData['ta_DiagnosisCode4@odatabind'] = {
						label: bvRecord?.woundOneDiagnosisCode1?.ta_name || '',
						value: bvRecord?.woundOneDiagnosisCode1?.ta_diagnosiscodeid || '',
					};
				}
				if (bvRecord?.ta_woundlength) {
					formData.ta_woundlength = Number(bvRecord?.ta_woundlength);
				}
				if (bvRecord?.ta_woundwidth) {
					formData.ta_woundwidth = Number(bvRecord?.ta_woundwidth);
				}
				if (bvRecord?.ta_wounddepth) {
					formData.ta_wounddepth = Number(bvRecord?.ta_wounddepth);
				}
				if (bvRecord?.ta_woundsizetotal2) {
					formData.ta_woundsizetotal2 = Number(bvRecord?.ta_woundsizetotal2);
				}
				if (bvRecord?.ta_woundtype1) {
					formData.ta_woundtype1 = {
						label: bvRecord?.ta_woundtype1,
						value: bvRecord?.ta_woundtype1,
					};
				}

				// Wound 2 fields
				if (
					bvRecord?.woundTwoApplicationCode1 ||
					bvRecord?.woundTwoApplicationCode2 ||
					bvRecord?.woundTwoDiagnosisCode1 ||
					bvRecord?.woundTwoDiagnosisCode2 ||
					bvRecord?.woundTwoDiagnosisCode3 ||
					bvRecord?.woundTwoDiagnosisCode4 ||
					bvRecord?.ta_wound2_woundlength ||
					bvRecord?.ta_wound2_woundwidth ||
					bvRecord?.ta_wound2_wounddepth ||
					bvRecord?.ta_wound2_woundsizetotal ||
					bvRecord?.ta_woundtype2
				) {
					formData.totalWoundsCreated = 2;
					if (bvRecord?.woundTwoApplicationCode1) {
						formData['ta_Wound2_Application1@odatabind'] = {
							label: bvRecord?.woundTwoApplicationCode1?.ta_name || '',
							value:
								bvRecord?.woundTwoApplicationCode1?.ta_applicationcodesid || '',
						};
					}
					if (bvRecord?.woundTwoApplicationCode2) {
						formData['ta_Wound2_Application2@odatabind'] = {
							label: bvRecord?.woundTwoApplicationCode2?.ta_name || '',
							value:
								bvRecord?.woundTwoApplicationCode2?.ta_applicationcodesid || '',
						};
					}
					if (bvRecord?.woundTwoDiagnosisCode1) {
						formData['ta_Wound2_Diagnosis1@odatabind'] = {
							label: bvRecord?.woundTwoDiagnosisCode1?.ta_name || '',
							value: bvRecord?.woundTwoDiagnosisCode1?.ta_diagnosiscodeid || '',
						};
						formData['ta_Wound2_Diagnosis2@odatabind'] = {
							label: bvRecord?.woundTwoDiagnosisCode1?.ta_name || '',
							value: bvRecord?.woundTwoDiagnosisCode1?.ta_diagnosiscodeid || '',
						};
						formData['ta_Wound2_Diagnosis3@odatabind'] = {
							label: bvRecord?.woundTwoDiagnosisCode1?.ta_name || '',
							value: bvRecord?.woundTwoDiagnosisCode1?.ta_diagnosiscodeid || '',
						};
						formData['ta_Wound2_Diagnosis4@odatabind'] = {
							label: bvRecord?.woundTwoDiagnosisCode1?.ta_name || '',
							value: bvRecord?.woundTwoDiagnosisCode1?.ta_diagnosiscodeid || '',
						};
					}
					if (bvRecord?.ta_wound2_woundlength) {
						formData.ta_wound2_woundlength = Number(
							bvRecord?.ta_wound2_woundlength,
						);
					}
					if (bvRecord?.ta_wound2_woundwidth) {
						formData.ta_wound2_woundwidth = Number(
							bvRecord?.ta_wound2_woundwidth,
						);
					}
					if (bvRecord?.ta_wound2_wounddepth) {
						formData.ta_wound2_wounddepth = Number(
							bvRecord?.ta_wound2_wounddepth,
						);
					}
					if (bvRecord?.ta_wound2_woundsizetotal) {
						formData.ta_wound2_woundsizetotal = Number(
							bvRecord?.ta_wound2_woundsizetotal,
						);
					}
					if (bvRecord?.ta_woundtype2) {
						formData.ta_woundtype2 = {
							label: bvRecord?.ta_woundtype2,
							value: bvRecord?.ta_woundtype2,
						};
					}
				}

				// Wound 3 fields
				if (
					bvRecord?.woundThreeApplicationCode1 ||
					bvRecord?.woundThreeApplicationCode2 ||
					bvRecord?.woundThreeDiagnosisCode1 ||
					bvRecord?.woundThreeDiagnosisCode2 ||
					bvRecord?.woundThreeDiagnosisCode3 ||
					bvRecord?.woundThreeDiagnosisCode4 ||
					bvRecord?.ta_wound3_woundlength ||
					bvRecord?.ta_wound3_woundwidth ||
					bvRecord?.ta_wound3_wounddepth ||
					bvRecord?.ta_wound3_woundsizetotal ||
					bvRecord?.ta_woundtype3
				) {
					formData.totalWoundsCreated = 3;
					if (bvRecord?.woundThreeApplicationCode1) {
						formData['ta_Wound3_Application1@odatabind'] = {
							label: bvRecord?.woundThreeApplicationCode1?.ta_name || '',
							value:
								bvRecord?.woundThreeApplicationCode1?.ta_applicationcodesid ||
								'',
						};
					}
					if (bvRecord?.woundThreeApplicationCode2) {
						formData['ta_Wound3_Application2@odatabind'] = {
							label: bvRecord?.woundThreeApplicationCode2?.ta_name || '',
							value:
								bvRecord?.woundThreeApplicationCode2?.ta_applicationcodesid ||
								'',
						};
					}
					if (bvRecord?.woundThreeDiagnosisCode1) {
						formData['ta_Wound3_Diagnosis1@odatabind'] = {
							label: bvRecord?.woundThreeDiagnosisCode1?.ta_name || '',
							value:
								bvRecord?.woundThreeDiagnosisCode1?.ta_diagnosiscodeid || '',
						};
						formData['ta_Wound3_Diagnosis2@odatabind'] = {
							label: bvRecord?.woundThreeDiagnosisCode1?.ta_name || '',
							value:
								bvRecord?.woundThreeDiagnosisCode1?.ta_diagnosiscodeid || '',
						};
						formData['ta_Wound3_Diagnosis3@odatabind'] = {
							label: bvRecord?.woundThreeDiagnosisCode1?.ta_name || '',
							value:
								bvRecord?.woundThreeDiagnosisCode1?.ta_diagnosiscodeid || '',
						};
						formData['ta_Wound3_Diagnosis4@odatabind'] = {
							label: bvRecord?.woundThreeDiagnosisCode1?.ta_name || '',
							value:
								bvRecord?.woundThreeDiagnosisCode1?.ta_diagnosiscodeid || '',
						};
					}
					if (bvRecord?.ta_wound3_woundlength) {
						formData.ta_wound3_woundlength = Number(
							bvRecord?.ta_wound3_woundlength,
						);
					}
					if (bvRecord?.ta_wound3_woundwidth) {
						formData.ta_wound3_woundwidth = Number(
							bvRecord?.ta_wound3_woundwidth,
						);
					}
					if (bvRecord?.ta_wound3_wounddepth) {
						formData.ta_wound3_wounddepth = Number(
							bvRecord?.ta_wound3_wounddepth,
						);
					}
					if (bvRecord?.ta_wound3_woundsizetotal) {
						formData.ta_wound3_woundsizetotal = Number(
							bvRecord?.ta_wound3_woundsizetotal,
						);
					}
					if (bvRecord?.ta_woundtype3) {
						formData.ta_woundtype3 = {
							label: bvRecord?.ta_woundtype3,
							value: bvRecord?.ta_woundtype3,
						};
					}
				}

				// Use reset() instead of multiple setValue calls to prevent form from being marked as dirty
				reset(formData, { keepDefaultValues: false, keepDirty: false });
			}
		});

	const { mutate: saveDraftMutation, isLoading: isSavingDraft, isEditingDraft } =
		useSaveDraft({
			existingDraft,
			onSuccess: () => {
				toast.success(
					existingDraft
						? 'Draft updated successfully'
						: 'Draft saved successfully',
				);
				onSuccess({ action: 'draft_saved' });
			},
		});

	useEffect(() => {
		if (hasExistingBenefitVerification) {
			getSingleBVDetails(benefitVerificationId);
		} else {
			// Set default value for new forms without marking as dirty
			setValue('totalWoundsCreated', 1, { shouldDirty: false });
		}
	}, [benefitVerificationId, hasExistingBenefitVerification, getSingleBVDetails, setValue]);

	useEffect(() => {
		if (existingDraft && !hasExistingBenefitVerification) {
			reset(existingDraft.formData, { keepDefaultValues: false, keepDirty: false });
		}
	}, [existingDraft, hasExistingBenefitVerification, reset]);

	const formMethods = {
		register,
		getValues,
		setValue,
		watch,
		errors,
		reset,
		control,
		setError,
	};

	// const {
	// 	mutate: createUpdateBV,
	// 	isLoading,
	// 	isSuccess,
	// 	isError,
	// } = useCreateUpdateBV(() => {
	// 	// success
	// 	setTimeout(() => {
	// 		onSuccess();
	// 	}, 2000);
	// });
	useEffect(() => {
		if (onIsDirtyChange) {
			onIsDirtyChange(!isDirty);
		}
	}, [isDirty]);

	const {
		mutate: createBgProcess,
		isLoading,
		isSuccess,
		isError,
	} = useCreateBgProcess(() => {
		setTimeout(() => {
			onSuccess({ action: 'submitted' });
		}, 2000);
	});

	const {
		mutate: updateBgProcess,
		isLoading: updateLoading,
		isError: updateError,
		isSuccess: updateSuccess,
	} = useUpdateBgProcess(() => {
		setTimeout(() => {
			onSuccess({ action: 'submitted' });
		}, 2000);
	});

	const handleStepChange = async (step: number) => {
		let isValid = true;
		if (step === 1) {
			isValid = await trigger(['clinic', 'provider', 'ta_servicelocation']);
		} else if (step === 2) {
			isValid = await trigger([
				'patient',
				'primaryInsurance',
				'ta_primarymemberid',
				'birthdate',
				'patientFirstName',
				'patientLastName',
			]);
		} else if (step === 3) {
			isValid = await trigger(['ta_productselection']);
		} else if (step === 4) {
			// Always validate Wound 1 fields
			const wound1Fields: (keyof BVFormData)[] = [
				'ta_ApplicationCode1@odatabind',
				'ta_DiagnosisCode1@odatabind',
				'ta_woundlength',
				'ta_woundwidth',
				'ta_proceduredate',
			];
			isValid = await trigger(wound1Fields);

			// Conditionally validate Wound 2 fields if wound 2 is added
			const totalWounds = watch('totalWoundsCreated') || 1;
			if (totalWounds >= 2 && isValid) {
				const wound2Fields: (keyof BVFormData)[] = [
					'ta_Wound2_Application1@odatabind',
					'ta_wound2_woundlength',
					'ta_wound2_woundwidth',
					'ta_wound2_wounddepth',
				];
				isValid = await trigger(wound2Fields);
			}

			// Conditionally validate Wound 3 fields if wound 3 is added
			if (totalWounds >= 3 && isValid) {
				const wound3Fields: (keyof BVFormData)[] = [
					'ta_wound3_woundlength',
					'ta_wound3_woundwidth',
					'ta_wound3_wounddepth',
				];
				isValid = await trigger(wound3Fields);
			}
		}

		if (!isValid) {
			return false;
		}

		return true;
	};

	const handleSaveDraft = useCallback(() => {
		const formData = getValues();

		// Generate titleOrReference from clinic name or patient name
		const clinicName = (formData.clinic as SelectItem)?.label || '';
		const patientName = (formData.patient as SelectItem)?.label || '';
		const titleOrReference = clinicName || patientName || 'Benefit Verification Draft';

		const payload = {
			userId: userId || '',
			userName: userName,
			formType: FORM_TYPES.BENEFIT_VERIFICATION,
			draftType: DRAFT_TYPE_LABELS.BENEFIT_VERIFICATION,
			titleOrReference,
			formData,
		};

		saveDraftMutation(payload);
	}, [getValues, isEditingDraft, saveDraftMutation, userId, userName]);

	useEffect(() => {
		if (!onExitConfirmationDraftActionChange) {
			return;
		}

		if (hasExistingBenefitVerification && !isEditingDraft) {
			onExitConfirmationDraftActionChange(null);
			return;
		}

		onExitConfirmationDraftActionChange({
			label: isEditingDraft ? 'Update Draft' : 'Save as Draft',
			onClick: handleSaveDraft,
			loading: isSavingDraft,
			disabled: isLoading || updateLoading || isSavingDraft,
		});

		return () => {
			onExitConfirmationDraftActionChange(null);
		};
	}, [
		handleSaveDraft,
		hasExistingBenefitVerification,
		isEditingDraft,
		isLoading,
		isSavingDraft,
		onExitConfirmationDraftActionChange,
		updateLoading,
	]);

	const handleSubmitForm = () => {
		const formData = getValues();
		trackCoreClick('submit_bv_clicked', {
			source_module: 'benefits_verification_form',
			form_mode: hasExistingBenefitVerification ? 'edit' : 'create',
			clinic_id: (formData.clinic as SelectItem | undefined)?.value,
			provider_id: (formData.provider as SelectItem | undefined)?.value,
			patient_id: (formData.patient as SelectItem | undefined)?.value,
			product_selection: formData.ta_productselection,
			total_wounds: formData.totalWoundsCreated,
		});
		
		// Calculate changedFields from dirtyFields for edit mode
		const calculateChangedFields = (): Array<{ label: string; value: any }> => {
			const changedFieldsArray: Array<{ label: string; value: any }> = [];
			
			// Helper to get display value for SelectItem fields
			const getDisplayValue = (fieldName: string, value: any): any => {
				if (value === null || value === undefined || value === '') {
					return value;
				}
				
				// Handle SelectItem objects
				if (typeof value === 'object' && 'label' in value) {
					return (value as SelectItem).label;
				}
				
				// Handle date formatting for date fields
				if (fieldName === 'birthdate' || fieldName === 'ta_proceduredate') {
					return value;
				}
				
				return value;
			};
			
			// Get all dirty field names from dirtyFields
			const dirtyFieldNames: string[] = [];
			
			const processDirtyFields = (dirty: any, prefix = '') => {
				if (!dirty || typeof dirty !== 'object') {
					return;
				}
				
				for (const key in dirty) {
					if (Object.hasOwn(dirty, key)) {
						const fullKey = prefix ? `${prefix}.${key}` : key;
						const value = dirty[key];
						
						// If value is true, the field is dirty
						if (value === true) {
							dirtyFieldNames.push(fullKey);
						}
						// If value is an object, recurse to check nested fields
						else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
							processDirtyFields(value, fullKey);
						}
					}
				}
			};
			
			processDirtyFields(dirtyFields);
			
			// Iterate through dirtyFields and track changes
			dirtyFieldNames.forEach((fieldName) => {
				const label = fieldLabelMap[fieldName];
				
				if (label) {
					const key = fieldName as keyof BVFormData;
					const value = formData[key];
					changedFieldsArray.push({
						label,
						value: getDisplayValue(fieldName, value),
					});
				}
			});
			
			return changedFieldsArray;
		};
		
		const changedFields = hasExistingBenefitVerification
			? calculateChangedFields()
			: [];
		
		const payload = {
			// ...(id ? { id } : {}),
			ta_bv_status: '953870000',
			ta_bvidfull: `TA-BV-${formData?.physicianLastName || ''}-${formData?.patientLastName || ''}`,
			ta_servicelocation: formData.ta_servicelocation
				? String((formData.ta_servicelocation as SelectItem).value)
				: null,
			'ta_Clinic@odata.bind': `accounts(${(formData?.clinic as SelectItem)?.value})`,
			'ta_Physician2@odata.bind': `contacts(${((formData?.provider as SelectItem)?.value as string).replace('{', '').replace('}', '')})`,
			'ta_Patient@odata.bind': `contacts(${(formData?.patient as SelectItem)?.value})`,
			'ta_PrimaryInsurance@odata.bind': `ta_insurances(${(formData?.primaryInsurance as SelectItem)?.value})`,
			'ta_SecondaryInsurance@odata.bind': formData?.secondaryInsurance && (formData?.secondaryInsurance as SelectItem)?.value
					? `ta_insurances(${(formData?.secondaryInsurance as SelectItem)?.value})`
					: null,
			ta_emrid: formData?.ta_emrid || null,
			ta_nursinghome: formData?.ta_nursinghome === 'yes' ? true : false,
		ta_100days: formData?.ta_100days === 'yes' ? true : false,
		ta_dob: convertToISODateFormat(formData?.birthdate),
		ta_primarymemberid: formData?.ta_primarymemberid || null,
			ta_secondarymemberid: formData?.ta_secondarymemberid || null,

			ta_noteshartreview: formData?.ta_noteshartreview || null,
			...(hasExistingBenefitVerification
				? {
						ta_productselection:
							formData?.ta_productselection !== formData?.oldProducts
								? formData?.ta_productselection
										?.split(',')
										.filter(
											(item) =>
												!getValues('oldProducts')?.split(',')?.includes(item),
										)
										.join(',')
								: formData?.ta_productselection,
					}
			: {
					ta_productselection: formData?.ta_productselection || null,
				}),
		ta_proceduredate: convertToISODateFormat(formData?.ta_proceduredate),
		ta_woundlength: formData?.ta_woundlength
				? String(formData?.ta_woundlength)
				: null,
			ta_woundwidth: formData?.ta_woundwidth
				? String(formData?.ta_woundwidth)
				: null,
			ta_wounddepth: formData?.ta_wounddepth
				? String(formData?.ta_wounddepth)
				: null,
			ta_woundsizetotal2: formData?.ta_woundsizetotal2
				? String(formData?.ta_woundsizetotal2)
				: null,
			ta_woundtype1: formData.ta_woundtype1
				? String((formData.ta_woundtype1 as SelectItem).value)
				: null,
			'ta_ApplicationCode1@odata.bind': formData?.[
				'ta_ApplicationCode1@odatabind'
			]
				? `ta_applicationcodeses(${(formData?.['ta_ApplicationCode1@odatabind'] as SelectItem)?.value})`
				: null,
			'ta_ApplicationCode2@odata.bind': formData?.[
				'ta_ApplicationCode2@odatabind'
			]
				? `ta_applicationcodeses(${(formData?.['ta_ApplicationCode2@odatabind'] as SelectItem)?.value})`
				: null,
			'ta_DiagnosisCode1@odata.bind': formData?.['ta_DiagnosisCode1@odatabind']
				? `ta_diagnosiscodes(${(formData?.['ta_DiagnosisCode1@odatabind'] as SelectItem)?.value})`
				: null,
			'ta_DiagnosisCode2@odata.bind': formData?.['ta_DiagnosisCode2@odatabind']
				? `ta_diagnosiscodes(${(formData?.['ta_DiagnosisCode2@odatabind'] as SelectItem)?.value})`
				: null,
			'ta_DiagnosisCode3@odata.bind': formData?.['ta_DiagnosisCode3@odatabind']
				? `ta_diagnosiscodes(${(formData?.['ta_DiagnosisCode3@odatabind'] as SelectItem)?.value})`
				: null,
			'ta_DiagnosisCode4@odata.bind': formData?.['ta_DiagnosisCode4@odatabind']
				? `ta_diagnosiscodes(${(formData?.['ta_DiagnosisCode4@odatabind'] as SelectItem)?.value})`
				: null,

			ta_wound2_woundlength: formData?.ta_wound2_woundlength
				? String(formData?.ta_wound2_woundlength)
				: null,
			ta_wound2_woundwidth: formData?.ta_wound2_woundwidth
				? String(formData?.ta_wound2_woundwidth)
				: null,
			ta_wound2_wounddepth: formData?.ta_wound2_wounddepth
				? String(formData?.ta_wound2_wounddepth)
				: null,
			ta_wound2_woundsizetotal: formData?.ta_wound2_woundsizetotal
				? String(formData?.ta_wound2_woundsizetotal)
				: null,
			ta_woundtype2: formData.ta_woundtype2
				? String((formData.ta_woundtype2 as SelectItem).value)
				: null,
			'ta_Wound2_Application1@odata.bind': formData?.[
				'ta_Wound2_Application1@odatabind'
			]
				? `ta_applicationcodeses(${(formData?.['ta_Wound2_Application1@odatabind'] as SelectItem)?.value})`
				: null,
			'ta_Wound2_Application2@odata.bind': formData?.[
				'ta_Wound2_Application2@odatabind'
			]
				? `ta_applicationcodeses(${(formData?.['ta_Wound2_Application2@odatabind'] as SelectItem)?.value})`
				: null,
			'ta_Wound2_Diagnosis1@odata.bind': formData?.[
				'ta_Wound2_Diagnosis1@odatabind'
			]
				? `ta_diagnosiscodes(${(formData?.['ta_Wound2_Diagnosis1@odatabind'] as SelectItem)?.value})`
				: null,
			'ta_Wound2_Diagnosis2@odata.bind': formData?.[
				'ta_Wound2_Diagnosis2@odatabind'
			]
				? `ta_diagnosiscodes(${(formData?.['ta_Wound2_Diagnosis2@odatabind'] as SelectItem)?.value})`
				: null,
			'ta_Wound2_Diagnosis3@odata.bind': formData?.[
				'ta_Wound2_Diagnosis3@odatabind'
			]
				? `ta_diagnosiscodes(${(formData?.['ta_Wound2_Diagnosis3@odatabind'] as SelectItem)?.value})`
				: null,
			'ta_Wound2_Diagnosis4@odata.bind': formData?.[
				'ta_Wound2_Diagnosis4@odatabind'
			]
				? `ta_diagnosiscodes(${(formData?.['ta_Wound2_Diagnosis4@odatabind'] as SelectItem)?.value})`
				: null,

			ta_wound3_woundlength: formData?.ta_wound3_woundlength
				? String(formData?.ta_wound3_woundlength)
				: null,
			ta_wound3_woundwidth: formData?.ta_wound3_woundwidth
				? String(formData?.ta_wound3_woundwidth)
				: null,
			ta_wound3_wounddepth: formData?.ta_wound3_wounddepth
				? String(formData?.ta_wound3_wounddepth)
				: null,
			ta_wound3_woundsizetotal: formData?.ta_wound3_woundsizetotal
				? String(formData?.ta_wound3_woundsizetotal)
				: null,
			ta_woundtype3: formData.ta_woundtype3
				? String((formData.ta_woundtype3 as SelectItem).value)
				: null,
			'ta_Wound3_Application1@odata.bind': formData?.[
				'ta_Wound3_Application1@odatabind'
			]
				? `ta_applicationcodeses(${(formData?.['ta_Wound3_Application1@odatabind'] as SelectItem)?.value})`
				: null,
			'ta_Wound3_Application2@odata.bind': formData?.[
				'ta_Wound3_Application2@odatabind'
			]
				? `ta_applicationcodeses(${(formData?.['ta_Wound3_Application2@odatabind'] as SelectItem)?.value})`
				: null,
			'ta_Wound3_Diagnosis1@odata.bind': formData?.[
				'ta_Wound3_Diagnosis1@odatabind'
			]
				? `ta_diagnosiscodes(${(formData?.['ta_Wound3_Diagnosis1@odatabind'] as SelectItem)?.value})`
				: null,
			'ta_Wound3_Diagnosis2@odata.bind': formData?.[
				'ta_Wound3_Diagnosis2@odatabind'
			]
				? `ta_diagnosiscodes(${(formData?.['ta_Wound3_Diagnosis2@odatabind'] as SelectItem)?.value})`
				: null,
			'ta_Wound3_Diagnosis3@odata.bind': formData?.[
				'ta_Wound3_Diagnosis3@odatabind'
			]
				? `ta_diagnosiscodes(${(formData?.['ta_Wound3_Diagnosis3@odatabind'] as SelectItem)?.value})`
				: null,
			'ta_Wound3_Diagnosis4@odata.bind': formData?.[
				'ta_Wound3_Diagnosis4@odatabind'
			]
				? `ta_diagnosiscodes(${(formData?.['ta_Wound3_Diagnosis4@odatabind'] as SelectItem)?.value})`
				: null,

			ta_portalbvupload: formData?.ta_portalbvupload || null,
			ta_originationsource: '953870002',
		};

		if (hasExistingBenefitVerification) {
			updateBgProcess({
				moduleName: BgProcessModule.BV,
				userId: userId || '',
				payload: {
					payload,
					additionalPayload: {
						patientLastName: formData?.patientLastName || '',
						physicianLastName: formData?.physicianLastName || '',
						clinicName: (formData?.clinic as SelectItem)?.label as string,
						changedFields: changedFields,
					},
				},
				crmId: benefitVerificationId,
			});
		} else {
			createBgProcess({
				moduleName: BgProcessModule.BV,
				userId: userId || '',
				payload: {
					payload,
					additionalPayload: {
						patientLastName: formData?.patientLastName || '',
						physicianLastName: formData?.physicianLastName || '',
						clinicName: (formData?.clinic as SelectItem)?.label as string,
					},
				},
			});
		}
	};

	const stepperSteps: StepperStep[] = [
		{
			name: 'Facility Information',
			element: <FacilityInformation formMethods={formMethods} />,
		},
		{
			name: 'Patient Information',
			element: <PatientInformation formMethods={formMethods} />,
		},
		{
			name: 'Product',
			element: <Product formMethods={formMethods} />,
		},
		{
			name: 'Wound Information',
			element: (
				<Box overflow={'auto'}>
					<WoundInformation formMethods={formMethods} />
				</Box>
			),
		},
		{
			name: 'Upload Documents',
			element: (
				<UploadDocuments
					data={{
						uploadedFiles: (watch('updatedFiles') as UploadedFile[]) || [],
					}}
					onChange={(_, value) => {
						setValue('updatedFiles', value.uploadedFiles);
						setValue(
							'ta_portalbvupload',
							value.uploadedFiles.at(-1)?.sharePointUrl || '',
						);
					}}
					clinicName={(watch('clinic') as SelectItem)?.label as string}
					clinicId={(watch('clinic') as SelectItem)?.value as string}
					folderName={`BV/${(watch('patient') as SelectItem)?.label as string}-${watch('ta_proceduredate') ? new Date(watch('ta_proceduredate')).toISOString().split('T').at(0) : ''}`}
				/>
			),
		},
		{
			name: 'Review',
			element: (
				<Box overflow={'auto'}>
					<Preview
						isCreationMode={true}
						bvInformation={{
							clinic: (watch('clinic') as SelectItem)?.label as string,
							clinicName: (watch('clinic') as SelectItem)?.label as string,
							providerName: (watch('provider') as SelectItem)?.label as string,
							patientName: (watch('patient') as SelectItem)?.label as string,
							birthdate: watch('birthdate') as string,
							primaryInsurance: (watch('primaryInsurance') as SelectItem)
								?.label as string,
							secondaryInsurance: (watch('secondaryInsurance') as SelectItem)
								?.label as string,
							primaryInsuranceMemberID: watch('ta_primarymemberid') as string,
							secondaryInsuranceMemberID: watch(
								'ta_secondarymemberid',
							) as string,
							emrId: watch('ta_emrid') as string,
							isPatientInNursingHome: watch('ta_nursinghome') as string,
							hasPatientHad100DaysOfCare: watch('ta_100days') as string,
							productSelection: watch('productSelectedNames') as string,
							procedureDate: watch('ta_proceduredate') as string,
							noteshartreview: watch('ta_noteshartreview') as string,
							wounds: [
								{
									applicationCode1: (
										watch('ta_ApplicationCode1@odatabind') as SelectItem
									)?.label as string,
									applicationCode2: (
										watch('ta_ApplicationCode2@odatabind') as SelectItem
									)?.label as string,
									woundDepth: watch('ta_wounddepth') as number,
									woundLength: watch('ta_woundlength') as number,
									woundWidth: watch('ta_woundwidth') as number,
									woundType: (watch('ta_woundtype1') as SelectItem)
										?.label as string,
									dCode1: (watch('ta_DiagnosisCode1@odatabind') as SelectItem)
										?.label as string,
									dCode2: (watch('ta_DiagnosisCode2@odatabind') as SelectItem)
										?.label as string,
									dCode3: (watch('ta_DiagnosisCode3@odatabind') as SelectItem)
										?.label as string,
									dCode4: (watch('ta_DiagnosisCode4@odatabind') as SelectItem)
										?.label as string,
								},
								{
									applicationCode1: (
										watch('ta_Wound2_Application1@odatabind') as SelectItem
									)?.label as string,
									applicationCode2: (
										watch('ta_Wound2_Application2@odatabind') as SelectItem
									)?.label as string,
									woundDepth: watch('ta_wound2_wounddepth') as number,
									woundLength: watch('ta_wound3_woundlength') as number,
									woundWidth: watch('ta_wound2_woundwidth') as number,
									woundType: (watch('ta_woundtype2') as SelectItem)
										?.label as string,
									dCode1: (
										watch('ta_Wound2_Diagnosis1@odatabind') as SelectItem
									)?.label as string,
									dCode2: (
										watch('ta_Wound2_Diagnosis2@odatabind') as SelectItem
									)?.label as string,
									dCode3: (
										watch('ta_Wound2_Diagnosis3@odatabind') as SelectItem
									)?.label as string,
									dCode4: (
										watch('ta_Wound2_Diagnosis4@odatabind') as SelectItem
									)?.label as string,
								},
								{
									applicationCode1: (
										watch('ta_Wound3_Application1@odatabind') as SelectItem
									)?.label as string,
									applicationCode2: (
										watch('ta_Wound3_Application2@odatabind') as SelectItem
									)?.label as string,
									woundDepth: watch('ta_wound3_wounddepth') as number,
									woundLength: watch('ta_wound3_woundlength') as number,
									woundWidth: watch('ta_wound3_woundwidth') as number,
									woundType: (watch('ta_woundtype3') as SelectItem)
										?.label as string,
									dCode1: (
										watch('ta_Wound3_Diagnosis1@odatabind') as SelectItem
									)?.label as string,
									dCode2: (
										watch('ta_Wound3_Diagnosis2@odatabind') as SelectItem
									)?.label as string,
									dCode3: (
										watch('ta_Wound3_Diagnosis3@odatabind') as SelectItem
									)?.label as string,
									dCode4: (
										watch('ta_Wound3_Diagnosis4@odatabind') as SelectItem
									)?.label as string,
								},
							],
							portalBVUploadUrl: watch('ta_portalbvupload') as string,
							totalWoundsCreated: watch('totalWoundsCreated'),
							uploadedFiles: watch('updatedFiles')?.map(
								(file) => file.name,
							) as string[],
						}}
					/>
				</Box>
			),
		},
	];

	return (
		<Box>
			<Show when={isLoadingSingleBV}>
				<FaceliftDataLoader
					title="Loading benefit verification..."
					description="Please wait while we fetch the benefit verification details."
				/>
			</Show>
			<Show when={!isLoadingSingleBV}>
				<Stepper
					steps={stepperSteps}
					controlButtonsPosition="left"
					onStepChange={(step) => {
						if ([1, 2, 3, 4, 5].includes(step)) {
							customFunction?.({ size: 'xl', step });
						} else {
							customFunction?.({ size: 'fullwidth', step }); //use custom indicator to expand full size of modal
						}
					}}
					validateStep={handleStepChange}
					submitButtonText="Submit"
					loading={isLoading || updateLoading}
					onSubmit={() => handleSubmitForm()}
					rightSideButtons={(currentStep, totalSteps) => (
						<Show
							when={
								currentStep === totalSteps &&
								(!hasExistingBenefitVerification || isEditingDraft)
							}
						>
							<FaceliftButton
								width={{ base: 'full', sm: '24', md: '28', lg: '40' }}
								bg="transparent"
								color="inherit"
								border="1px solid"
								onClick={handleSaveDraft}
								loading={isSavingDraft}
								disabled={isLoading || updateLoading || isSavingDraft}
							>
								{isEditingDraft ? 'Update Draft' : 'Save as Draft'}
							</FaceliftButton>
						</Show>
					)}
				/>
				<FaceliftSuccessProgressModal
					open={isLoading || updateLoading || isSuccess || isError}
					isLoading={isLoading || updateLoading}
					isSucceed={isSuccess || updateSuccess}
					hasError={isError || updateError}
					onOpenChange={() => {}}
					title={
						hasExistingBenefitVerification
							? 'Benefit verification updated successfully.'
							: 'Benefit verification submitted successfully.'
					}
					errorMessage={
						isError
							? hasExistingBenefitVerification
								? 'Error updating benefit verification'
								: 'Error submitting benefit verification'
							: ''
					}
					{
						...(
							(
								(getValues('primaryInsurance') as SelectItem)?.label?.toLowerCase() === 'medicare (part b)' ||
								(getValues('secondaryInsurance') as SelectItem)?.label?.toLowerCase() === 'medicare (part b)'
							)
								? {
										successCustomMessage: `Preliminary Verification successfully submitted for Patient: ${
											(getValues('patient') as SelectItem).label
										}. More details available within the Action Column of the BV Menu. Thank you!`,
									}
								: {}
						)
					}
				/>
			</Show>
		</Box>
	);
};

export default BenefitVerification;
export type { BenefitVerificationData };
